import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import { DateTime } from '@ksyos/date-time';
import {
    Modal,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ModelAsDto } from '@common/contexts/support/domain/types/modelType';
import type { DeviceLog } from '@common/interfaces/deviceLog';
import { getDisplayName } from '@common/utils/getDisplayName';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IDevice } from './DeviceDetails';

type DevicesInformationLogbookModalProps = {
    device: Pick<IDevice, 'id'>;
    isOpen: boolean;
    onClose: () => void;
};

export function DevicesInformationLogbookModal(props: Readonly<DevicesInformationLogbookModalProps>) {
    const translate = getSelectedLanguage();

    const { device, isOpen, onClose } = props;

    const abortSignal = useAbortSignal();
    const [logs, setLogs] = useState<DeviceLog[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        if (isOpen) {
            getLogs();
        }
    }, [isOpen, currentPage, pageSize]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xlarge"
        >
            <Modal.Header>{translate.devices.logbookModal.title}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translate.devices.logbookModal.date}</TableHeader>
                                <TableHeader>{translate.devices.logbookModal.modifiedBy}</TableHeader>
                                <TableHeader>{translate.devices.type}</TableHeader>
                                <TableHeader>{translate.devices.serialNumber}</TableHeader>
                                <TableHeader>{translate.devices.notes}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell>{entry.created_at.format('DD-MM-YYYY')}</TableCell>
                                    <TableCell>
                                        {entry.modifierPractitioner ? getDisplayName(entry.modifierPractitioner) : '-'}
                                    </TableCell>
                                    <TableCell>{entry.deviceType?.name ?? null ?? '-'}</TableCell>
                                    <TableCell>{entry.externalId ?? '-'}</TableCell>
                                    <TableCell>{['', null].includes(entry.notes) ? '-' : entry.notes}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Pagination
                    onChange={handlePageChange}
                    page={currentPage}
                    pageSize={pageSize}
                    totalItems={total}
                />
            </Modal.Body>

            <Modal.Footer />
        </Modal>
    );

    async function handlePageChange(paginationEvent: PaginationEventHandlersParams) {
        setPageSize(paginationEvent.pageSize);
        setCurrentPage(paginationEvent.page);
        getLogs();
    }

    async function getLogs() {
        const offset = (currentPage - 1) * pageSize;

        const response = await fetchJson<{ items: ModelAsDto<DeviceLog>[]; total: number }>({
            method: 'GET',
            signal: abortSignal,
            url: `/api/devices/${device.id}/logs?limit=${pageSize}&offset=${offset}`,
        });

        const items = response.json?.items ?? [];

        const data = items.map((item) => ({
            ...item,
            created_at: DateTime.from(item.created_at),
            updated_at: DateTime.from(item.updated_at),
        }));

        setLogs(data);
        setTotal(response.json?.total ?? 0);
    }
}
